import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { UserLiteData } from '../../api/ApiTypes';
import { uuid } from '../../CodesThatsGottaGoSomewhereElse';
import { Colors, Input, Menu } from '../../ui';
import { PickerMenu } from '../../ui/PickerMenu';
import { FlexScroller } from '../../ui/Scrollers';
import { AppLocation } from '../AppLocation';
import { useResolve } from '../Container';
import { Copy, Flexer, Title } from '../dashboard/elements/LayoutElements';
import { ProfileModel } from '../ProfileModel';
import { Avatar } from './Avatar';

const useUserQuery = (query: string) => {
    const api = useResolve(ApiClient);
    const [debouncedQuery] = useDebounce(query, 400);

    return useQuery({
        queryKey: ['users.list.like', debouncedQuery],
        queryFn() {
            if (!debouncedQuery) return [];
            return api.exec(v2.users.list({ like: debouncedQuery, count: 8 }));
        },
        enabled: !!debouncedQuery,
    });
};

const useInitialUsers = (initial: UserLiteData[] | 'auto' = 'auto') => {

    const api = useResolve(ApiClient);
    const location = useResolve(AppLocation);
    const workspaceId = location.get('workspace');
    const token = useMemo(() => uuid(), [workspaceId, initial]);

    const { data } = useQuery({
        queryKey: ['SelectUserView', token],
        queryFn() {

            if (Array.isArray(initial)) return initial;
            if (initial === 'auto') {
                return api.exec(v2.users.list({ scope: workspaceId, count: 20 }));
            }

            return [];
        },
    });

    return data ?? [];
};

export type SelectUserViewProps = {
    title?: string,
    initial?: UserLiteData[] | 'auto',
    search?: string,
    allowKeyboard?: boolean,
    allowSearch?: boolean,
    onSelect: (user: UserLiteData) => void,
};

const useRenderItem = () => {
    const me = useResolve(ProfileModel);
    return useMemo(() =>
        (item: UserLiteData, index: number) => <>
            <Avatar.ForUser size="1.5" user={item} />
            <Menu.ItemTextCropped>{item.name}</Menu.ItemTextCropped>
            {me.id === item.id && <Menu.Shortcut keys="You" />}
        </>,
        [],
    );
};

export const SelectUserView = ({ title, initial, search, allowKeyboard, allowSearch, onSelect }: SelectUserViewProps) => {
    const [searchInput, setSearchInput] = useState('');
    search = search ?? searchInput;

    const { data: results, isLoading } = useUserQuery(search);
    const initialUsers = useInitialUsers(initial);
    const list = !!search ? (results ?? []) : (initialUsers ?? []);

    const onSelectFromMenu = useCallback(({ id }) => {
        onSelect(list.find(x => x.id === id));
    }, [onSelect, list]);

    const renderItem = useRenderItem();

    return <Flexer flex="column" width="18" height="16.25">
        {title &&
            <Flexer padding=".5 1" background={Colors.gray50} borderRadius=".5rem .5rem 0 0" borderBottom={`1px solid ${Colors.borderLight}`}>
                <Title flush size="2xs" color={Colors.text} uppercase weight="500">{title}</Title>
            </Flexer>
        }
        {allowSearch &&
            <Flexer padding=".5">
                <Input autoFocus compact placeholder="Search..." value={search} onChange={setSearchInput} />
            </Flexer>
        }
        <FlexScroller>
            <PickerMenu allowKeyboard={allowKeyboard} items={list} renderItem={renderItem} onAction={onSelectFromMenu} />
            {!isLoading && !!search && list.length === 0 &&
                <Flexer grow flex center>
                    <Copy flush>Sorry, no matches!</Copy>
                </Flexer>
            }
        </FlexScroller>
        <Flexer height=".5" />
    </Flexer>;
};
